import { v7 as uuidv7 } from 'uuid'
import { soundForMessageSent } from '../sound/notify'
import { api } from '../api/endpoints'
import { ApiError, errorMessage, isPermanentFailure } from '../api/client'
import type { AnswerRef, ChannelEvent, ChecklistReasonInput, MessageContext, SendMessageBody } from '../api/types'
import { db, type OutboxAttachment, type OutboxRow } from '../db/db'
import { currentMeId } from '../state/session'
import { isOnline } from '../state/connection'
import { toast } from '../state/toasts'
import { applyEvents } from './applyEvents'
import { syncChannel } from './syncEngine'

export interface ComposeInput {
  channelId: string
  body: string
  attachments?: OutboxAttachment[]
  replyToId?: string | null
  /** A private reply in a bot conversation: the channel message it answers. */
  context?: MessageContext | null
  /** Tapping one of a bot's options: the question and the option (the body is the option's label). */
  answer?: AnswerRef | null
  /** A checklist's Done: why each item not ticked was not done. */
  reasons?: ChecklistReasonInput[] | null
}

/** Exponential backoff with a 60 s ceiling: 2s, 4s, 8s, ... */
export function backoffMs(attempts: number): number {
  return Math.min(60_000, 1000 * 2 ** Math.max(1, attempts))
}

type Sender = (channelId: string, body: SendMessageBody) => Promise<ChannelEvent>
let sender: Sender = (channelId, body) => api.sendMessage(channelId, body)

/** Test seam. */
export function setOutboxSender(next: Sender): void {
  sender = next
}

export async function enqueueMessage(input: ComposeInput): Promise<OutboxRow> {
  const attachments = input.attachments ?? []
  const row: OutboxRow = {
    id: uuidv7(),
    channelId: input.channelId,
    body: input.body,
    attachmentIds: attachments.map((a) => a.id),
    attachments,
    replyToId: input.replyToId ?? null,
    context: input.context ?? null,
    answer: input.answer ?? null,
    reasons: input.reasons ?? null,
    createdAt: new Date().toISOString(),
    attempts: 0,
    state: 'queued',
    nextAttemptAt: 0,
    error: null,
    confirmedSeq: null,
  }
  await db.outbox.add(row)
  kickOutbox()
  return row
}

export async function retryOutboxRow(id: string): Promise<void> {
  await db.outbox.update(id, { state: 'queued', nextAttemptAt: 0, error: null })
  kickOutbox()
}

export async function deleteOutboxRow(id: string): Promise<void> {
  await db.outbox.delete(id)
}

/** Rows left "sending" by a closed tab go back to the queue. */
export async function resetStuckOutbox(): Promise<void> {
  await db.outbox.where('state').equals('sending').modify({ state: 'queued', nextAttemptAt: 0 })
}

async function nextDueRow(now: number): Promise<OutboxRow | undefined> {
  const queued = await db.outbox.where('state').equals('queued').sortBy('createdAt')
  return queued.find((row) => row.confirmedSeq === null && row.nextAttemptAt <= now)
}

async function earliestRetry(): Promise<number | null> {
  const queued = await db.outbox.where('state').equals('queued').toArray()
  const waiting = queued.filter((r) => r.confirmedSeq === null).map((r) => r.nextAttemptAt)
  return waiting.length ? Math.min(...waiting) : null
}

/** Send one row. Exported for tests. */
export async function sendRow(row: OutboxRow): Promise<'sent' | 'retry' | 'failed'> {
  const meId = currentMeId()
  if (!meId) return 'retry'
  await db.outbox.update(row.id, { state: 'sending' })
  const body: SendMessageBody = { id: row.id, body: row.body }
  if (row.attachmentIds.length) body.attachmentIds = row.attachmentIds
  if (row.replyToId) body.replyToId = row.replyToId
  if (row.context) body.context = row.context
  if (row.answer) body.answer = row.answer
  if (row.reasons) body.reasons = row.reasons
  try {
    const event = await sender(row.channelId, body)
    const [outcome] = await applyEvents([event], meId)
    if (outcome === 'applied' || outcome === 'duplicate') {
      // applied: the event handler already removed the row. duplicate: the hub delivered it first.
      const exists = await db.messages.get(row.id)
      if (exists || outcome === 'applied') await db.outbox.delete(row.id)
      else await db.outbox.update(row.id, { state: 'queued', confirmedSeq: event.seq })
    } else {
      // Accepted by the server but there is a gap locally: keep rendering it until sync fills in.
      await db.outbox.update(row.id, { state: 'queued', confirmedSeq: event.seq })
      void syncChannel(row.channelId)
    }
    void soundForMessageSent()
    return 'sent'
  } catch (error) {
    // Answered on another device, expired, or closed by its bot: the tap is moot. Drop it and show the question as it is.
    if (row.answer && error instanceof ApiError && (error.error === 'question_answered' || error.error === 'question_closed')) {
      await db.outbox.delete(row.id)
      toast(row.reasons ? 'This checklist has closed' : 'This question has closed')
      void syncChannel(row.channelId)
      return 'failed'
    }
    if (isPermanentFailure(error)) {
      await db.outbox.update(row.id, { state: 'failed', error: errorMessage(error), attempts: row.attempts + 1 })
      return 'failed'
    }
    const attempts = row.attempts + 1
    const retryAfterMs = error instanceof ApiError && error.retryAfter ? error.retryAfter * 1000 : 0
    await db.outbox.update(row.id, {
      state: 'queued',
      attempts,
      nextAttemptAt: Date.now() + Math.max(backoffMs(attempts), retryAfterMs),
      error: errorMessage(error),
    })
    return 'retry'
  }
}

let running = false
let rerun = false
let timer: ReturnType<typeof setTimeout> | null = null

async function drain(): Promise<void> {
  if (running) {
    rerun = true
    return
  }
  running = true
  try {
    do {
      rerun = false
      while (isOnline() && currentMeId()) {
        const row = await nextDueRow(Date.now())
        if (!row) break
        const result = await sendRow(row)
        if (result === 'retry') break // likely offline; wait for backoff or the online event
      }
    } while (rerun)
  } finally {
    running = false
  }
  const next = await earliestRetry()
  if (timer) clearTimeout(timer)
  timer = null
  if (next !== null) timer = setTimeout(() => kickOutbox(), Math.max(250, next - Date.now()))
}

/** Wake the foreground sender (after compose, on 'online', on hub reconnect). */
export function kickOutbox(): void {
  void drain().catch((error) => console.warn('outbox drain failed', error))
}

/** Delay before the next retry, forcing an immediate attempt for waiting rows (used on 'online'). */
export async function retryAllNow(): Promise<void> {
  await db.outbox.where('state').equals('queued').modify({ nextAttemptAt: 0 })
  kickOutbox()
}
